/**
 * SS Feedback Components — Spinner, EmptyState, ErrorBanner, SuccessBanner, InfoBanner
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage:
 *   import { Spinner, EmptyState, ErrorBanner, SuccessBanner } from './feedback';
 */
import React from 'react';
import { COLOR, FONT, RADIUS } from './tokens';

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ label = 'Loading…', size = 'md' }) {
  const pad = size === 'sm' ? '12px 0' : '40px 0';
  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      gap:            10,
      padding:        pad,
      color:          COLOR.textMuted,
      fontSize:       FONT.size.base,
    }}>
      <span style={{
        display:      'inline-block',
        width:        size === 'sm' ? 18 : 26,
        height:       size === 'sm' ? 18 : 26,
        border:       `3px solid ${COLOR.grayBorder}`,
        borderTop:    `3px solid ${COLOR.blue}`,
        borderRadius: '50%',
        animation:    'ss-spin 0.7s linear infinite',
      }} />
      {label && <span>{label}</span>}
      {/* Keyframe injected once via a style tag */}
      <style>{`@keyframes ss-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
/**
 * @param {string}  icon       - emoji or svg
 * @param {string}  title      - bold heading
 * @param {string}  message    - softer sub-text
 * @param {React.ReactNode} action  - optional CTA button
 */
export function EmptyState({ icon = '📭', title = 'Nothing here', message, action }) {
  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '48px 24px',
      textAlign:      'center',
      gap:            8,
    }}>
      {icon && (
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: '#F1F5FA',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, marginBottom: 4,
        }}>
          {icon}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: FONT.weight.bold, color: COLOR.text }}>{title}</div>
      {message && <div style={{ fontSize: FONT.size.base, color: '#8b7681', maxWidth: 360 }}>{message}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

// ── Banner (shared base) ──────────────────────────────────────────────────────

/**
 * Defensive child renderer: API error payloads sometimes reach banners as raw
 * objects/arrays (e.g. FastAPI 422 `[{type, loc, msg, input}]`). Rendering
 * those as React children throws and takes the whole page tree down — and the
 * crash-recovery remount makes every input lose focus per keystroke. Convert
 * anything non-renderable to a readable string instead.
 */
function safeChildren(children) {
  if (children == null || typeof children === 'string' || typeof children === 'number') return children;
  if (React.isValidElement(children)) return children;
  if (Array.isArray(children)) {
    if (children.every((c) => c == null || typeof c === 'string' || typeof c === 'number' || React.isValidElement(c))) {
      return children;
    }
    return children
      .map((d) => {
        if (d == null) return '';
        if (typeof d === 'string' || typeof d === 'number') return String(d);
        if (React.isValidElement(d)) return '';
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || JSON.stringify(d));
      })
      .filter(Boolean)
      .join(' · ');
  }
  if (typeof children === 'object') {
    return children.msg || children.message || JSON.stringify(children);
  }
  return String(children);
}

function Banner({ bg, border, textColor, icon, children, onClose }) {
  const content = safeChildren(children);
  if (!content || (Array.isArray(content) && content.length === 0)) return null;
  return (
    <div style={{
      background:   bg,
      border:       `1px solid ${border}`,
      color:        textColor,
      borderRadius: RADIUS.md,
      padding:      '10px 14px',
      fontSize:     FONT.size.base,
      marginBottom: 14,
      display:      'flex',
      alignItems:   'flex-start',
      gap:          8,
    }}>
      {icon && <span style={{ flexShrink: 0, fontSize: 15 }}>{icon}</span>}
      <span style={{ flex: 1 }}>{content}</span>
      {onClose && (
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: textColor, opacity: 0.6, fontSize: 16, lineHeight: 1, padding: 0,
        }}>×</button>
      )}
    </div>
  );
}

export function ErrorBanner({ children, onClose }) {
  return (
    <Banner
      bg={COLOR.redBg} border={COLOR.redBorder} textColor={COLOR.redDark}
      icon="⚠️" onClose={onClose}
    >
      {children}
    </Banner>
  );
}

export function SuccessBanner({ children, onClose }) {
  return (
    <Banner
      bg={COLOR.greenBg} border={COLOR.greenBorder} textColor={COLOR.greenDark}
      icon="✅" onClose={onClose}
    >
      {children}
    </Banner>
  );
}

export function InfoBanner({ children, onClose }) {
  return (
    <Banner
      bg={COLOR.blueBg} border={COLOR.blueBorder} textColor={COLOR.blue}
      icon="ℹ️" onClose={onClose}
    >
      {children}
    </Banner>
  );
}

export function WarnBanner({ children, onClose }) {
  return (
    <Banner
      bg={COLOR.amberBg} border={COLOR.amberBorder} textColor={COLOR.amberDark}
      icon="⚡" onClose={onClose}
    >
      {children}
    </Banner>
  );
}
